import React, { useContext } from 'react';

import getVirtualComponent from './getVirtualComponent';
import { useFormSelector } from '../../custom-hooks';
import { FORM_MODEL_ATTRIBUTES, SS_CONTROL_TYPE } from '../constants/form-manager.constants';
import { FormContext } from '../context';
import {
  getEvaluatedConditions,
  getIndexForControl,
  getModelById,
  getPropByModel,
} from '../selectors/form-manager.selectors';
import { equalityFn } from '../utils/form-manager.utils';

// handles generic controller actions
const VirtualDynamicField = ({
  id,
  index: parentIndex = [0],
  isParentHidden = false,
  isParentViewOnly = false,
  containersProps,
  ...rest
}) => {
  const formKey = useContext(FormContext);
  const {
    childrenNodes,
    controlType,
    entityName,
    fieldName,
    parent,
    ...modelProps
  } = useFormSelector(getModelById, { id });

  const parentType = useFormSelector(getPropByModel, {
    id: parent,
    prop: FORM_MODEL_ATTRIBUTES.CONTROL_TYPE,
  });

  const index = useFormSelector(getIndexForControl, { id, parentIndex }, equalityFn);
  const selectorProps = { id, index, entityName };

  const isControlViewOnly = useFormSelector(getEvaluatedConditions, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.VIEW_ONLY,
  });
  const isViewOnly = isControlViewOnly || isParentViewOnly;

  const isControlHidden = useFormSelector(getEvaluatedConditions, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.HIDDEN,
  });
  const isHidden = isControlHidden || isParentHidden;

  // map through childrenNodes and create an array of DynamicFields from it
  const children =
    ![SS_CONTROL_TYPE.GRID, SS_CONTROL_TYPE.REPEATING_PANEL].includes(controlType) &&
    childrenNodes &&
    childrenNodes.map((childKey) => (
      <VirtualMemoizedField
        key={`${childKey}#${index.join(',')}`}
        id={childKey}
        index={index}
        isParentHidden={isHidden}
        isParentViewOnly={isViewOnly}
        containersProps={containersProps}
      />
    ));

  // declare the component props
  const props = {
    ...rest,
    ...modelProps,
    containersProps,
    entityName,
    fieldName,
    childrenNodes,
    id,
    index,
    children,
    isHidden,
    formKey,
    parentType,
    isViewOnly,
    parent,
    controlType,
    parentIndex,
  };

  return getVirtualComponent(props);
};

const VirtualMemoizedField = React.memo(VirtualDynamicField);

export default VirtualMemoizedField;
